import { createInertiaApp } from '@inertiajs/react';
import type { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryProvider } from '../providers/QueryProvider';
import { reportError } from '../utils/reportError';

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ReactNode;
  layout?: (page: ReactNode) => ReactNode;
};

// Promise のrejectionをキャッチ
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

  reportError(error, {
    type: 'unhandledrejection',
    originalReason: event.reason,
  });
});

// JavaScript エラーをキャッチ
window.addEventListener('error', (event: ErrorEvent) => {
  const error = event.error || new Error(event.message);

  reportError(error, {
    type: 'javascript-error',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>(['../pages/**/*.tsx', '!../pages/debug_panel/**/*.tsx'], {
      eager: true,
    });
    const page = pages[`../pages/${name}.tsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page) => createElement(Layout, null, page)

    return page;
  },

  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(
        <QueryProvider>
          <App {...props} />
        </QueryProvider>,
      );
    } else {
      console.error(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      );
    }
  },
});
